import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import useDebouncedValue from '../hooks/useDebouncedValue';
import {
  fetchActivePartById,
  mergePartSearchResults,
  searchActiveParts
} from '../utils/catalogSearch';

const NEW_SENT = '__new_part__';

function partOptionLabel(p) {
  if (!p || p._isNew) return 'New Part…';
  const num = p.part_number != null ? String(p.part_number).trim() : '';
  const desc = String(p.part_description ?? p.description ?? '').trim();
  if (!num && !desc) return '';
  return desc ? `${num || '—'} — ${desc}` : num || '';
}

/**
 * Searchable part picker for quote/order line items (server-side search).
 * @param {string|number} valueId - parts.id or '' (never __new_part__ as persisted value)
 * @param {(raw: string, part?: object|null) => void} onCommit
 */
export default function LineItemPartAutocomplete({
  valueId,
  onCommit,
  size = 'small',
  disabled = false,
  label = 'Part',
  showNewPart = true,
  textFieldSx,
  noMargin = false
}) {
  const newOpt = useMemo(() => ({ id: NEW_SENT, _isNew: true }), []);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedPart, setResolvedPart] = useState(null);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    let cancelled = false;
    if (valueId == null || String(valueId).trim() === '') {
      setResolvedPart(null);
      return undefined;
    }
    if (resolvedPart && String(resolvedPart.id) === String(valueId)) {
      return undefined;
    }
    (async () => {
      const part = await fetchActivePartById(valueId);
      if (!cancelled) setResolvedPart(part);
    })();
    return () => {
      cancelled = true;
    };
  }, [valueId, resolvedPart]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedInput.trim();
    if (!q) {
      setOptions(resolvedPart ? [resolvedPart] : []);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const results = await searchActiveParts(q);
        if (cancelled) return;
        setOptions(mergePartSearchResults(results, resolvedPart));
      } catch (err) {
        console.error('Part search failed:', err);
        if (!cancelled) setOptions(resolvedPart ? [resolvedPart] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedInput, resolvedPart]);

  const listOptions = useMemo(
    () => (showNewPart ? [newOpt, ...options] : [...options]),
    [showNewPart, newOpt, options]
  );

  const selected = useMemo(() => {
    if (valueId == null || String(valueId).trim() === '') return null;
    if (resolvedPart && String(resolvedPart.id) === String(valueId)) return resolvedPart;
    return options.find((p) => String(p.id) === String(valueId)) || null;
  }, [valueId, resolvedPart, options]);

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      options={listOptions}
      value={selected}
      loading={loading}
      filterOptions={(opts) => opts}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'reset') return;
        setInputValue(newInputValue);
      }}
      onChange={(_e, v) => {
        if (!v) {
          onCommit('', null);
          return;
        }
        if (v._isNew) {
          onCommit(NEW_SENT, null);
          return;
        }
        setResolvedPart(v);
        onCommit(String(v.id), v);
      }}
      getOptionLabel={(opt) => partOptionLabel(opt)}
      isOptionEqualToValue={(a, b) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return String(a.id) === String(b.id);
      }}
      noOptionsText={
        loading ? 'Searching…' : inputValue.trim() ? 'No parts found' : 'Type to search parts'
      }
      renderOption={(props, opt) => (
        <li {...props} key={opt._isNew ? NEW_SENT : opt.id}>
          {partOptionLabel(opt)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          // Table cells already have a column header; floating labels bleed into the row above.
          label={noMargin ? undefined : label}
          placeholder="Search parts…"
          InputLabelProps={noMargin ? undefined : { ...params.InputLabelProps, shrink: true }}
          inputProps={{
            ...params.inputProps,
            'aria-label': label || 'Part'
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={textFieldSx}
          margin={noMargin ? 'none' : 'dense'}
        />
      )}
      ListboxProps={{ style: { maxHeight: 280 } }}
    />
  );
}
